import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

/**
 * Read-only detector for the Broker "Getting Started" checklist.
 * Mirrors useShipperGettingStartedProgress: SELECT counts only, no writes.
 *
 * Steps:
 *   1. network        — joined at least one shipper's private network
 *   2. creditAppTpl   — has a broker_credit_app_templates row
 *   3. firstBid       — placed at least one direct_bid
 *   4. firstWin       — has at least one accepted bid
 *   5. dispatchTouch  — has an awarded dispatch (any status past draft)
 */
export interface BrokerGettingStartedProgress {
  network: boolean;
  creditAppTpl: boolean;
  firstBid: boolean;
  firstWin: boolean;
  dispatchTouch: boolean;
}

const EMPTY: BrokerGettingStartedProgress = {
  network: false,
  creditAppTpl: false,
  firstBid: false,
  firstWin: false,
  dispatchTouch: false,
};

export function useBrokerGettingStartedProgress(userId: string | null | undefined) {
  const [progress, setProgress] = useState<BrokerGettingStartedProgress>(EMPTY);
  const [loaded, setLoaded] = useState(false);

  const check = useCallback(async () => {
    if (!userId) {
      setProgress(EMPTY);
      setLoaded(true);
      return;
    }

    const sb = supabase as any;

    // Resolve the broker's active company (credit app templates are company-scoped).
    const { data: cu } = await sb
      .from("company_users")
      .select("company_id")
      .eq("user_id", userId)
      .eq("is_active", true)
      .limit(1)
      .maybeSingle();
    const companyId = (cu?.company_id as string | null) ?? null;

    const tplFilter = companyId
      ? `company_id.eq.${companyId},uploaded_by.eq.${userId}`
      : `uploaded_by.eq.${userId}`;

    const results: Array<{ count: number | null }> = await Promise.all([
      sb.from("shipper_network_brokers")
        .select("id", { count: "exact", head: true })
        .eq("broker_user_id", userId)
        .eq("status", "accepted"),
      sb.from("broker_credit_app_templates")
        .select("id", { count: "exact", head: true })
        .or(tplFilter),
      sb.from("direct_bids")
        .select("id", { count: "exact", head: true })
        .eq("broker_id", userId),
      sb.from("direct_bids")
        .select("id", { count: "exact", head: true })
        .eq("broker_id", userId)
        .eq("status", "accepted"),
      sb.from("direct_dispatches")
        .select("id", { count: "exact", head: true })
        .eq("awarded_broker_id", userId)
        .neq("status", "draft"),
    ]);
    const [networkRes, tplRes, bidRes, winRes, dispatchRes] = results;

    setProgress({
      network: (networkRes.count ?? 0) > 0,
      creditAppTpl: (tplRes.count ?? 0) > 0,
      firstBid: (bidRes.count ?? 0) > 0,
      firstWin: (winRes.count ?? 0) > 0,
      dispatchTouch: (dispatchRes.count ?? 0) > 0,
    });
    setLoaded(true);
  }, [userId]);

  useEffect(() => {
    check();
    const onFocus = () => check();
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onFocus);
    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onFocus);
    };
  }, [check]);

  return { progress, loaded, refetch: check };
}
